<template>
  <div class="plan-board-container">
    <div class="header-container">
      <div class="header">
        SERVICE PLANS
      </div>
      <div class="filter-container">
        <button
          :class="[
            'filter-button',
            activeFilter === '1week' ? 'active' : null,
          ]"
          @click="changeDateFilter('1week')"
        >
          1 week
        </button>
        <button
          :class="[
            'filter-button',
            activeFilter === '1month' ? 'active' : null,
          ]"
          @click="changeDateFilter('1month')"
        >
          1 month
        </button>
        <button
          :class="[
            'filter-button',
            activeFilter === '6months' ? 'active' : null,
          ]"
          @click="changeDateFilter('6months')"
        >
          6 months
        </button>
        <button
          :class="[
            'filter-button',
            activeFilter === '1year' ? 'active' : null,
          ]"
          @click="changeDateFilter('1year')"
        >
          1 year
        </button>
        <button
          :class="[
            'filter-button',
            activeFilter === 'allTime' ? 'active' : null,
          ]"
          @click="changeDateFilter('allTime')"
        >
          All Time
        </button>
        <Datepicker
          v-model="dateRange"
          mode="range"
          :max-date="new Date()"
          popover-align="right"
        >
          <template #default>
            <button
              :class="[
                'filter-button',
                activeFilter === 'custom' ? 'active' : null,
              ]"
            >
              <div v-if="activeFilter !== 'custom'">
                Custom
                <img
                  src="@shared/assets/img/general/chevron-grey-down.png"
                  alt="Select"
                  class="button-icon"
                >
              </div>
              <span v-else>
                {{ customRangeHeader }}
              </span>
              <div
                v-if="activeFilter === 'custom' && dateRange.start && dateRange.end"
                class="clear-container"
              >
                <div class="clear-tooltip">
                  Clear Dates
                </div>
                <button
                  class="clear-btn"
                  @click="clearRangeFilter"
                >
                  <img
                    class="button-icon"
                    src="@shared/assets/img/general/x-white.png"
                    alt="Clear"
                  >
                </button>
              </div>
            </button>
          </template>
        </Datepicker>
      </div>
    </div>
    <div
      v-if="plans.length > 0"
      class="sub-header-container"
    >
      <div class="sub-header">
        PLAN
      </div>
      <div class="sub-header">
        CUSTOMERS
      </div>
    </div>
    <div class="plan-body-container">
      <div
        v-if="loading"
        class="hero-number"
      >
        <img
          src="@shared/assets/img/general/loading-white.svg"
          alt="Loading"
        >
      </div>
      <div
        v-else-if="plans.length === 0"
        class="empty-text"
      >
        No Service Plans
      </div>
      <BoardItem
        v-for="plan in plans"
        v-else
        :key="plan.planId || plan.planName"
        :header="plan.planName"
        :number="plan.count"
        :change="plan.change"
      />
    </div>
  </div>
</template>

<script>
import Datepicker from 'v-calendar/lib/components/date-picker.umd';
import moment from 'moment';
import apiClient from '@shared/lib/general/apiClient';
import adoptionMixin from '@shared/mixins/adoptionMixin';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';
import adminMixin from '@shared/mixins/adminMixin';
import BoardItem from './board/BoardItem.vue';

export default {
  name: 'PlanBoard',
  components: {
    BoardItem,
    Datepicker,
  },
  mixins: [adminMixin, adoptionMixin, unitPreferencesMixin],
  data() {
    return {
      planOverview: [],
      plans: [],
      activeFilter: '1week',
      customRange: {
        start: null,
        end: null,
      },
      loading: false,
    };
  },
  computed: {
    dateRange: {
      get() {
        return this.customRange;
      },
      set(dateRange) {
        if (dateRange && dateRange.start && dateRange.end) {
          this.customRange = dateRange;
          this.activeFilter = 'custom';
          this.getPlans();
        }
      },
    },
    customRangeHeader() {
      const {
        start,
        end,
      } = this.customRange;

      const {
        dateFormat,
      } = this;

      if (start && end) {
        let startFormat = dateFormat === 'M/D' ? 'MMM D' : 'D MMM';
        if (!moment(start).isSame(end, 'year')) {
          startFormat = dateFormat === 'M/D' ? 'MMM D \'YY' : 'D MMM \'YY';
        }
        const endFormat = dateFormat === 'M/D' ? 'MMM D \'YY' : 'D MMM \'YY';
        const rangeStart = moment(start).format(startFormat);
        const rangeEnd = moment(end).format(endFormat);
        return `${rangeStart} - ${rangeEnd}`;
      }
      return null;
    },
  },
  watch: {
    activeFilter(filter) {
      if (filter === 'custom') {
        this.getPlans();
      } else {
        this.setFilter();
      }
    },
    selectedCorpId() {
      this.getPlans();
      this.getPlansOverview();
    },
  },
  mounted() {
    if (this.selectedCorpId) {
      this.getPlans();
      this.getPlansOverview();
    }
  },
  methods: {
    clearRangeFilter() {
      this.customRange = {
        start: null,
        end: null,
      };
      this.activeFilter = '1year';
    },
    async getPlansOverview() {
      this.loading = true;
      try {
        const { data } = await apiClient({
          method: 'GET',
          url: `/adoption/stat/plans/${this.selectedCorpId}/overview`,
        });
        this.planOverview = data;
        this.setFilter();
      } catch (err) {
        console.error(err);
      }
      this.loading = false;
    },
    async getPlans() {
      const {
        start,
        end,
      } = this.customRange;
      if (start && end) {
        this.loading = true;
        try {
          const startQuery = moment(start).format('YYYY-MM-DD');
          const endQuery = moment(end).format('YYYY-MM-DD');
          const { data } = await apiClient({
            method: 'GET',
            url: `/adoption/stat/plans/${this.selectedCorpId}/start/${startQuery}/end/${endQuery}`,
          });
          this.plans = data;
        } catch (err) {
          console.error(err);
        }
        this.loading = false;
      }
    },
    setFilter() {
      const {
        activeFilter,
        planOverview,
      } = this;
      const newPlans = planOverview.map((overview) => {
        const {
          planName,
          lastSevenDays,
          prevSevenDays,
          lastThirtyDays,
          prevThirtyDays,
          lastSixMonths,
          prevSixMonths,
          lastTwelveMonths,
          prevTwelveMonths,
          allTime,
        } = overview;
        let change = 'none';
        let count = 0;
        if (activeFilter === '1week') {
          if (lastSevenDays < prevSevenDays) {
            change = 'down';
          } else if (lastSevenDays > prevSevenDays) {
            change = 'up';
          }
          count = lastSevenDays;
        }
        if (activeFilter === '1month') {
          if (lastThirtyDays < prevThirtyDays) {
            change = 'down';
          } else if (lastThirtyDays > prevThirtyDays) {
            change = 'up';
          }
          count = lastThirtyDays;
        }
        if (activeFilter === '6months') {
          if (lastSixMonths < prevSixMonths) {
            change = 'down';
          } else if (lastSixMonths > prevSixMonths) {
            change = 'up';
          }
          count = lastSixMonths;
        }
        if (activeFilter === '1year') {
          if (lastTwelveMonths < prevTwelveMonths) {
            change = 'down';
          } else if (lastTwelveMonths > prevTwelveMonths) {
            change = 'up';
          }
          count = lastTwelveMonths;
        }
        if (activeFilter === 'allTime') {
          count = allTime;
        }
        return {
          planName,
          count,
          change,
        };
      });
      this.plans = newPlans.sort((a, b) => b.count - a.count);
    },
    changeDateFilter(date) {
      this.activeFilter = date;
      if (date !== 'custom') {
        this.customRange = {
          start: null,
          end: null,
        };
      }
    },
  },
};
</script>

<style lang="scss" scoped>

.plan-board-container {
  width: 100%;
  margin-top: 34px;
  background-color: $N95;
  padding: 16px 24px 24px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;

  .header-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;

    .header {
      font-size: 0.875rem;
      color: $WHITE;
      font-weight: 700;
    }

    .filter-container {
      display: flex;

      .filter-button {
        font-size: 0.875rem;
        font-weight: 500;
        color: $N60;
        text-decoration: underline;
        margin-left: 24px;
        display: flex;
        align-items: center;

        &.active {
          font-weight: 700;
          color: $WHITE;
          text-decoration: none;
        }

        .button-icon {
          width: 12px;
          margin-left: 5px;
          object-fit: contain;
        }

        .clear-container {
          position: relative;

          &:hover {
            .clear-tooltip {
              opacity: 1;
            }
          }

          .clear-btn {
            display: flex;
          }

          .clear-tooltip {
            position: absolute;
            top: -20px;
            left: -26px;
            opacity: 0;
            background-color: $WHITE;
            color: $N100;
            font-size: 0.625rem;
            padding: 2px 4px;
            border-radius: 4px;
            white-space: nowrap;

            &:hover {
              opacity: 1;
            }
          }
        }
      }
    }
  }

  .sub-header-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;

    .sub-header {
      font-size: 0.75rem;
      color: $WHITE;
      font-weight: 600;
    }
  }

  .plan-body-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-height: 300px;
    overflow: scroll;
    -ms-overflow-style: none;
    /* IE and Edge */
    scrollbar-width: none;

    /* Firefox */
    &::-webkit-scrollbar {
      display: none;
    }

    .empty-text {
      color: $WHITE;
      font-weight: 500;
    }
  }
}
</style>
